/**
 * GreenVertus — Cross-sell v20.1
 *
 * ARCHITECTURE :
 * - Chaque conteneur .gv-cs[data-product-id] est autonome : il fetch ses propres
 *   recommandations selon son data-intent ('complementary' avec fallback 'related').
 * - Drawer : ajout → /cart/add.js → fetch section → cart:update → morphSection → re-render.
 * - Page panier classique (.cart-container custom) : ajout → re-fetch /cart → remplace la liste.
 * - Fiche produit : conteneur 'product' rendu par cart-enhancements (Souvent achetés ensemble).
 * - Cache des recos par produit+intent, anti-flicker par token (pid + contenu panier).
 * - v20.1 : pont de compatibilité pour les anciens widgets "Elite" incrustés dans
 *   certains templates produit — leurs événements cart:updated / cart:refresh sont
 *   traduits vers le véritable cart:update Horizon (drawer + bulle natives à jour).
 */
(function () {
  'use strict';

  const state = { cartIds: [], ready: false };
  const recoCache = {};
  let _ignoreNextCartUpdate = false;

  function money(cents) {
    return (cents / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
  }

  async function fetchCart() {
    const r = await fetch('/cart.js', { cache: 'no-store' });
    if (!r.ok) throw new Error();
    const cart = await r.json();
    state.cartIds = cart.items.map(i => i.product_id);
    return cart;
  }

  async function fetchRecoFor(pid, intent) {
    const key = pid + ':' + intent;
    if (recoCache[key]) return recoCache[key];
    let products = [];
    try {
      const r = await fetch(`/recommendations/products.json?product_id=${pid}&limit=8&intent=${intent}`);
      if (r.ok) products = (await r.json()).products || [];
    } catch { }
    // Fallback : pas de produits complémentaires configurés → produits similaires
    if (!products.length && intent === 'complementary') {
      return fetchRecoFor(pid, 'related');
    }
    recoCache[key] = products;
    return products;
  }

  function buildCard(product) {
    const v        = product.variants[0];
    const price    = v.price;
    const compare  = v.compare_at_price;
    const hasPromo = compare && compare > price;
    const pct      = hasPromo ? Math.round((1 - price / compare) * 100) : 0;
    const raw      = product.featured_image || '';
    const imgUrl   = raw ? (raw.includes('?') ? raw + '&width=400' : raw + '?width=400') : null;
    const title    = product.title.replace(/"/g, '&quot;');
    
    // Construction du selecteur de variantes si nécessaire
    let variantHtml = '';
    if (product.variants && product.variants.length > 1) {
      variantHtml = `
        <div class="gv-cs__variant-wrap">
          <select class="gv-cs__variant-select" aria-label="Sélectionner une option">
            ${product.variants.map(variant => `
              <option value="${variant.id}" data-price="${variant.price}" data-compare="${variant.compare_at_price || ''}" ${!variant.available ? 'disabled' : ''}>
                ${variant.title} ${!variant.available ? '(Épuisé)' : ''}
              </option>
            `).join('')}
          </select>
          <svg aria-hidden="true" focusable="false" class="gv-cs__variant-icon" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path></svg>
        </div>
      `;
    }

    const card = document.createElement('article');
    card.className = 'gv-cs__card';
    card.setAttribute('role', 'listitem');
    card.innerHTML = [
      hasPromo ? `<span class="gv-cs__badge">-${pct}%</span>` : '',
      `<a href="${product.url}" class="gv-cs__img-wrap" tabindex="-1" aria-hidden="true">`,
        imgUrl
          ? `<img class="gv-cs__img" src="${imgUrl}" alt="${title}" loading="lazy" width="400" height="400">`
          : `<div class="gv-cs__img-placeholder">🌿</div>`,
      '</a>',
      '<div class="gv-cs__body">',
        `<a href="${product.url}" class="gv-cs__name">${product.title}</a>`,
        '<div class="gv-cs__price-row">',
          `<span class="gv-cs__price">${money(price)}</span>`,
          hasPromo ? `<span class="gv-cs__compare">${money(compare)}</span>` : '',
          hasPromo ? `<span class="gv-cs__savings">Vous économisez ${pct}%</span>` : '',
        '</div>',
        variantHtml,
        `<button class="gv-cs__cta" type="button" data-variant-id="${v.id}" data-product-id="${product.id}" aria-label="Ajouter ${title} au panier" ${!v.available ? 'disabled' : ''}>`,
          !v.available ? '<span>Épuisé</span>' : '<span>Ajouter au panier</span>',
        '</button>',
      '</div>',
    ].join('');

    // Listener sur le bouton ajout
    card.querySelector('.gv-cs__cta').addEventListener('click', e => {
      e.preventDefault();
      e.stopPropagation();
      addToCart(e.currentTarget);
    });

    // Listener sur la carte (lien général)
    card.addEventListener('click', e => {
      if (!e.target.closest('.gv-cs__cta') && !e.target.closest('.gv-cs__variant-select')) {
        window.location.href = product.url;
      }
    });

    // Listener sur le selecteur de variantes
    const select = card.querySelector('.gv-cs__variant-select');
    if (select) {
      select.addEventListener('change', e => {
        const option = e.target.options[e.target.selectedIndex];
        const btn = card.querySelector('.gv-cs__cta');
        const priceEl = card.querySelector('.gv-cs__price');
        const compareEl = card.querySelector('.gv-cs__compare');
        const savingsEl = card.querySelector('.gv-cs__savings');
        
        btn.dataset.variantId = option.value;
        const newPrice = parseInt(option.dataset.price, 10);
        const newCompare = option.dataset.compare ? parseInt(option.dataset.compare, 10) : null;
        
        if (priceEl) priceEl.innerHTML = money(newPrice);
        
        let hasPromoNow = false;
        if (newCompare && newCompare > newPrice) {
          hasPromoNow = true;
          const newPct = Math.round((1 - newPrice / newCompare) * 100);
          
          if (!compareEl) {
            const row = card.querySelector('.gv-cs__price-row');
            row.insertAdjacentHTML('beforeend', `<span class="gv-cs__compare">${money(newCompare)}</span><span class="gv-cs__savings">Vous économisez ${newPct}%</span>`);
          } else {
            compareEl.style.display = '';
            compareEl.innerHTML = money(newCompare);
            if (savingsEl) {
              savingsEl.style.display = '';
              savingsEl.innerHTML = `Vous économisez ${newPct}%`;
            } else {
              compareEl.insertAdjacentHTML('afterend', `<span class="gv-cs__savings">Vous économisez ${newPct}%</span>`);
            }
          }
        } else {
          if (compareEl) compareEl.style.display = 'none';
          if (savingsEl) savingsEl.style.display = 'none';
        }
      });
    }

    return card;
  }

  function showSkeleton(track) {
    if (!track.querySelector('.gv-cs__skeleton-card')) {
      track.innerHTML = '<div class="gv-cs__skeleton">' +
        '<div class="gv-cs__skeleton-card"></div>'.repeat(4) +
        '</div>';
    }
  }

  async function renderContainer(container) {
    const track = container.querySelector('.gv-cs__track');
    if (!track) return;

    const pid = container.dataset.productId;
    if (!pid) return;
    const intent = container.dataset.intent || 'related';

    if (!state.ready) { showSkeleton(track); return; }

    // Anti-flicker : ne re-render que si le produit ou le panier a changé
    const token = pid + '|' + intent + '|' + state.cartIds.join(',');
    if (container.dataset.gvToken === token && track.querySelector('.gv-cs__card')) return;
    container.dataset.gvToken = token;

    const products = await fetchRecoFor(pid, intent);
    if (container.dataset.gvToken !== token) return; // rendu obsolète entre-temps

    const selfId = parseInt(pid, 10);
    const filtered = products
      .filter(p => p.id !== selfId && !state.cartIds.includes(p.id))
      .slice(0, 4);

    if (!filtered.length) { container.style.display = 'none'; return; }

    container.style.display = '';
    track.innerHTML = '';
    track.style.opacity = '0';
    track.style.transition = 'opacity .25s ease';
    filtered.forEach(p => track.appendChild(buildCard(p)));
    requestAnimationFrame(() => { track.style.opacity = '1'; });
  }

  function renderAll() {
    document.querySelectorAll('.gv-cs[data-product-id]').forEach(c => { renderContainer(c); });
  }

  const adding = new Set();

  async function addToCart(btn) {
    const variantId  = btn.dataset.variantId;
    const productId  = parseInt(btn.dataset.productId, 10);

    if (adding.has(variantId)) return;
    adding.add(variantId);

    btn.setAttribute('data-loading', '');
    btn.innerHTML = '<span class="gv-cs__cta-icon">⏳</span><span>Ajout...</span>';

    try {
      // Étape 1 : ajoute au panier
      const addRes = await fetch('/cart/add.js', {
        method : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body   : JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      if (!addRes.ok) throw new Error();
      const addData = await addRes.json();

      // Étape 2 : met à jour les cartIds optimistement
      if (!state.cartIds.includes(productId)) state.cartIds.push(productId);

      // Étape 3 : trouve le sectionId du drawer
      const cartItemsComp = document.querySelector('cart-items-component[data-drawer]');
      const sectionId = cartItemsComp?.dataset?.sectionId;

      if (sectionId) {
        // Drawer : fetch le HTML de section mis à jour et dispatch cart:update
        const sectRes = await fetch(`/?sections=${sectionId}`);
        if (!sectRes.ok) throw new Error();
        const sections = await sectRes.json();

        _ignoreNextCartUpdate = true;

        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: addData,
            sourceId: 'gv-crosssell',
            data: {
              sections,
              itemCount: addData.item_count,
            },
          },
        }));

        setTimeout(renderAll, 350);

      } else {
        // Pas de drawer (ex : page panier classique) → rafraîchit la liste custom
        const cartContainer = document.querySelector('.cart-container');
        if (cartContainer) {
          try {
            const pageRes = await fetch('/cart', { cache: 'no-store' });
            const pageHtml = await pageRes.text();
            const doc = new DOMParser().parseFromString(pageHtml, 'text/html');
            const fresh = doc.querySelector('.cart-container');
            if (fresh) cartContainer.innerHTML = fresh.innerHTML;
          } catch { /* silencieux */ }
        }
        renderAll();
      }

      // Feedback visuel sur le bouton
      btn.removeAttribute('data-loading');
      btn.setAttribute('data-added', '');
      btn.innerHTML = '<span class="gv-cs__cta-icon">✓</span><span>Ajouté !</span>';

    } catch {
      btn.removeAttribute('data-loading');
      btn.innerHTML = '<span class="gv-cs__cta-icon">＋</span><span>Ajouter</span>';
    } finally {
      adding.delete(variantId);
    }
  }

  async function preload() {
    try {
      await fetchCart();
      state.ready = true;
      renderAll();
    } catch (e) { console.warn('[GV CS] preload', e); }
  }

  async function refreshAndRender() {
    try {
      await fetchCart();
      state.ready = true;
      renderAll();
    } catch (e) { console.warn('[GV CS] refresh', e); }
  }

  // API publique
  window.gvCsRenderAll = renderAll;
  window.gvCsInit      = refreshAndRender;

  // Boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', preload);
  } else {
    preload();
  }

  // Écoute cart:update natif (changement de quantité, suppression, etc.)
  document.addEventListener('cart:update', async () => {
    if (_ignoreNextCartUpdate) {
      _ignoreNextCartUpdate = false;
      setTimeout(renderAll, 350);
      return;
    }
    try {
      await fetchCart();
      state.ready = true;
      setTimeout(renderAll, 350);
    } catch { }
  });

  // ── Pont de compatibilité anciens widgets Elite (copies inline) ────────
  // Ces widgets dispatchent cart:updated / cart:refresh après un /cart/add.js,
  // événements que le thème Horizon n'écoute pas. On traduit vers cart:update
  // avec le HTML de la section drawer → drawer + bulle natifs se mettent à jour.
  let _lastLegacyBridge = 0;
  async function legacyBridge(e) {
    if (e && e.detail && (e.detail.sourceId === 'gv-legacy-bridge' || e.detail.sourceId === 'gv-crosssell')) return;
    if (Date.now() - _lastLegacyBridge < 600) return; // les widgets legacy tirent 4 événements d'affilée
    _lastLegacyBridge = Date.now();
    try {
      const cart = await fetchCart();
      state.ready = true;
      const comp = document.querySelector('cart-items-component[data-drawer]');
      const sectionId = comp?.dataset?.sectionId;
      const detailData = { itemCount: cart.item_count };
      if (sectionId) {
        const sres = await fetch(`/?sections=${sectionId}`);
        if (sres.ok) detailData.sections = await sres.json();
      }
      _ignoreNextCartUpdate = true;
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: cart, sourceId: 'gv-legacy-bridge', data: detailData },
      }));
      setTimeout(renderAll, 350);
    } catch { }
  }
  document.addEventListener('cart:updated', legacyBridge);
  document.addEventListener('cart:refresh', legacyBridge);

  // Ouverture drawer → render immédiat
  new MutationObserver(mutations => {
    for (const m of mutations) {
      if (
        m.type === 'attributes' &&
        m.attributeName === 'open' &&
        m.target instanceof HTMLDialogElement &&
        m.target.open
      ) {
        setTimeout(renderAll, 100);
        break;
      }
    }
  }).observe(document.body, { attributes: true, subtree: true, attributeFilter: ['open'] });

  // Fallback nouveaux nodes (morph du drawer, remplacement de la liste panier, etc.)
  new MutationObserver(mutations => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (
          node.matches?.('.gv-cs[data-product-id]') ||
          node.querySelector?.('.gv-cs[data-product-id]') ||
          node.matches?.('.gv-cs__skeleton') ||
          node.querySelector?.('.gv-cs__skeleton')
        ) {
          setTimeout(renderAll, 100);
          return;
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

})();
